import { useMemo } from "react"
import { BarChart3, Gauge, Grid3x3, LineChart, ListTree, Table2, Building2 } from "lucide-react"
import { FilterBar, en } from "procurement/design"
import { useGetActiveBudgetCategoriesQuery, useGetBudgetCentersQuery } from "shared/redux/endpoints/procurementV2"
import { Page, Card } from "./ui"
import useProcurementFilters from "./hooks/useProcurementFilters"
import { FISCAL_MONTH_OPTIONS } from "./constants"
import OverviewKpis from "./components/budgetReport/OverviewKpis"
import CategoryBudgetChart from "./components/budgetReport/CategoryBudgetChart"
import CenterAllocationTree from "./components/budgetReport/CenterAllocationTree"
import CenterSpendingChart from "./components/budgetReport/CenterSpendingChart"
import BudgetHeatmap from "./components/budgetReport/BudgetHeatmap"
import SpendingTrendChart from "./components/budgetReport/SpendingTrendChart"
import BudgetDetailTable from "./components/budgetReport/BudgetDetailTable"
import "./components/budgetReport/budgetReport.css"

const now = new Date()
const currentYear = now.getFullYear()
const defaultMonth = String(now.getMonth() + 1)
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) => {
  const y = currentYear + 1 - i
  return { value: String(y), label: String(y) }
})
// Every dashboard endpoint needs a specific month — no "Full year".
const MONTH_OPTIONS = FISCAL_MONTH_OPTIONS.filter((o) => o.value)

const BUDGET_KEYS = { year: "fiscalYear", month: "fiscalMonth", center: "centerId", category: "categoryId" }
const RECORD_KEYS = { year: "recFiscalYear", month: "recFiscalMonth", center: "recCenterId", category: "recCategoryId" }

// `records` tabs (B3/B7) work at OR line × PO × Payment Request level; the
// rest aggregate budget by Year × Month × Category × Center — separate filters.
const TABS = [
  {
    key: "overview",
    label: "Overview",
    icon: Gauge,
    title: "Budget overview",
    subtitle: "Total, committed, unpaid, paid and remaining budget for the period.",
    Component: OverviewKpis,
  },
  {
    key: "category",
    label: "By category",
    icon: BarChart3,
    title: "Budget vs actual by category",
    subtitle: "Share of each category's budget used. Bold line = 100% of budget; red = over budget.",
    Component: CategoryBudgetChart,
  },
  {
    key: "center",
    label: "By center",
    icon: Building2,
    title: "Spending by center",
    subtitle: "Total budget vs used, with used % on the right axis.",
    Component: CenterSpendingChart,
  },
  {
    key: "heatmap",
    label: "Heatmap",
    icon: Grid3x3,
    title: "Center × category heatmap",
    subtitle: "Used % per cell — hover a cell for the amounts.",
    Component: BudgetHeatmap,
  },
  {
    key: "trend",
    label: "Trend",
    icon: LineChart,
    title: "Monthly spending trend",
    subtitle: "Cumulative spending since the start of the year vs planned budget.",
    Component: SpendingTrendChart,
  },
  {
    key: "allocation",
    label: "Allocation",
    icon: ListTree,
    title: "Budget allocation by center",
    subtitle: "Center → category budget; expand a category for item OR references (not summed).",
    Component: CenterAllocationTree,
    records: true,
    flush: true,
  },
  {
    key: "detail",
    label: "Detail",
    icon: Table2,
    title: "Detail records",
    subtitle: "OR lines, POs and payment links. PO / payment amounts cover the whole PO — don't add them up.",
    Component: BudgetDetailTable,
    records: true,
    flush: true,
  },
]

const buildFields = ({ centers, categories, keys }) => [
  { key: keys.year, label: en.filter.fiscalYear, type: "select", options: YEAR_OPTIONS, allowEmpty: false },
  { key: keys.month, label: en.filter.month, type: "select", options: MONTH_OPTIONS, allowEmpty: false },
  {
    key: keys.center,
    label: en.filter.center,
    type: "select",
    placeholder: en.filter.allCenters,
    options: centers.map((c) => ({ value: String(c.id), label: c.name })),
  },
  {
    key: keys.category,
    label: en.filter.category,
    type: "select",
    placeholder: en.filter.allCategories,
    options: categories.map((c) => ({ value: String(c.id), label: c.name })),
  },
]

const BudgetReportContainer = () => {
  const view = useProcurementFilters({ tab: "overview" })
  const budget = useProcurementFilters({
    fiscalYear: String(currentYear),
    fiscalMonth: defaultMonth,
    centerId: "",
    categoryId: "",
  })
  const records = useProcurementFilters({
    recFiscalYear: String(currentYear),
    recFiscalMonth: defaultMonth,
    recCenterId: "",
    recCategoryId: "",
  })
  const { data: centers = [] } = useGetBudgetCentersQuery({ isActive: true })
  const { data: categories = [] } = useGetActiveBudgetCategoriesQuery({ isActive: true })

  const { fiscalYear, fiscalMonth, centerId, categoryId } = budget.filters
  const params = useMemo(
    () => ({ fiscalYear, fiscalMonth, centerId, categoryId }),
    [fiscalYear, fiscalMonth, centerId, categoryId]
  )
  const { recFiscalYear, recFiscalMonth, recCenterId, recCategoryId } = records.filters
  const recordParams = useMemo(
    () => ({ fiscalYear: recFiscalYear, fiscalMonth: recFiscalMonth, centerId: recCenterId, categoryId: recCategoryId }),
    [recFiscalYear, recFiscalMonth, recCenterId, recCategoryId]
  )

  const active = TABS.find((t) => t.key === view.filters.tab) || TABS[0]
  const filter = active.records ? records : budget
  const { Component, icon: Icon } = active

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={["Procurement", "Reports"]}
      title="Budget Reports"
      subtitle="Budget vs actual spending across centers and categories"
    >
      <div className="br-tabs" role="tablist" aria-label="Report views">
        {TABS.map((t) => {
          const TabIcon = t.icon
          const selected = t.key === active.key
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`br-tab${selected ? " is-active" : ""}`}
              onClick={() => view.setFilters({ tab: t.key === "overview" ? "" : t.key })}
            >
              <TabIcon size={16} aria-hidden />
              {t.label}
            </button>
          )
        })}
      </div>

      <FilterBar
        key={active.records ? "records" : "budget"}
        title={active.records ? "Order & payment filters" : "Budget filters"}
        values={filter.filters}
        onChange={filter.setFilters}
        onReset={filter.resetFilters}
        fields={buildFields({ centers, categories, keys: active.records ? RECORD_KEYS : BUDGET_KEYS })}
      />

      <Card icon={<Icon aria-hidden />} title={active.title} subtitle={active.subtitle} flush={active.flush}>
        <Component params={active.records ? recordParams : params} />
      </Card>
    </Page>
  )
}

export default BudgetReportContainer
